/**
 * PurposeBadge — TagBadge wrapper that renders the purpose enum with
 * a consistent color + icon. Drives the eyebrow chip on the sandbox header
 * and the body of the Purpose card on the Overview tab.
 */
import React from 'react';
import { TagBadge, type TagColor } from '@chatsdet/ui';
import { GitPullRequest, Pencil, Question, TerminalWindow, TestTube } from '@phosphor-icons/react';
import type { EnvironmentInstancePurpose } from '../../../hooks/useEnvironmentInstances';

type PurposeConfig = {
  label: string;
  color: TagColor;
  icon: React.ReactNode | null;
};

const PURPOSE_CONFIG: Record<EnvironmentInstancePurpose, PurposeConfig> = {
  authoring: { label: 'Authoring', color: 'blue', icon: <Pencil size={12} weight="fill" /> },
  dev_session: { label: 'Dev session', color: 'gray', icon: <TerminalWindow size={12} weight="fill" /> },
  pr_test: { label: 'PR test', color: 'orange', icon: <GitPullRequest size={12} weight="fill" /> },
  test_run: { label: 'Test run', color: 'purple', icon: <TestTube size={12} weight="fill" /> },
  flow_authoring: { label: 'Sandbox env', color: 'teal', icon: <Pencil size={12} weight="fill" /> },
  unknown: { label: 'Unknown', color: 'gray', icon: null },
};

export interface PurposeBadgeProps {
  purpose: EnvironmentInstancePurpose;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function PurposeBadge({ purpose, size = 'md', className }: PurposeBadgeProps) {
  const config = PURPOSE_CONFIG[purpose] ?? PURPOSE_CONFIG.unknown;
  return (
    <TagBadge
      name={config.label}
      color={config.color}
      size={size}
      icon={config.icon ?? <Question size={12} />}
      className={className}
    />
  );
}

PurposeBadge.displayName = 'PurposeBadge';
